import { useEffect, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { LOGOUT_REDIRECT_FLAG, useAuth } from "@/features/auth/AuthContext"
import { sanitizeReturnUrl } from "@/features/auth/returnUrl"
import { SESSION_EXPIRED_FLAG } from "@/features/auth/sessionExpired"
import { LoadingBlock } from "@/widgets/LoadingBlock"

type RequireAuthProps = {
  children: ReactNode
}

/** Редирект на /login с безопасным returnUrl, пока нет токена */
export const RequireAuth = ({ children }: RequireAuthProps) => {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (isAuthenticated) return
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG) === "1") return
    const shouldRedirectHome = sessionStorage.getItem(LOGOUT_REDIRECT_FLAG) === "1"
    if (shouldRedirectHome) {
      sessionStorage.removeItem(LOGOUT_REDIRECT_FLAG)
      navigate("/", { replace: true })
      return
    }
    const path = `${location.pathname}${location.search}`
    const safe = sanitizeReturnUrl(path, import.meta.env.BASE_URL ?? "/")
    const returnUrl = safe ?? "/"
    navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`, { replace: true })
  }, [isAuthenticated, navigate, location.pathname, location.search])

  if (!isAuthenticated) {
    return <LoadingBlock variant="page" message={t("common.loading")} rootStyle={{ minHeight: "40vh" }} />
  }

  return children
}
